/**
 * Maps verbose system message text to compact, user-friendly labels.
 * Non-technical users see clean pills; power users can tap to expand.
 */

export interface HumanizedEvent {
  icon: string;
  label: string;
  /** Original text shown on expand */
  detail: string;
  /** Category for grouping/styling */
  category: 'refresh' | 'continuation' | 'save' | 'info' | 'error';
}

const PATTERNS: Array<{ re: RegExp; icon: string; label: string; category: HumanizedEvent['category'] }> = [
  // Manual context reset outcome markers (saved to DB after the context-reset: divider)
  { re: /^context-refreshed-with-save$/,                      icon: '✓',  label: 'Session refreshed — knowledge preserved', category: 'save' },
  { re: /^context-refreshed-no-save$/,                        icon: '↺',  label: 'Session reset — no notes saved',           category: 'refresh' },

  // Auto-continuation — MUST be before session-reset patterns because the
  // continuation message contains "session reset" which would match first otherwise
  { re: /auto[- ]?continu/i,                                  icon: '↻',  label: 'Continuing...',           category: 'continuation' },
  { re: /continuing\s+where/i,                                icon: '↻',  label: 'Continuing...',           category: 'continuation' },
  { re: /picking\s+up\s+where/i,                              icon: '↻',  label: 'Continuing...',           category: 'continuation' },

  // Context threshold / session reset
  { re: /context\s+(at|reached|usage)\s+\d+%/i,              icon: '⏳', label: 'Session refreshed',       category: 'refresh' },
  { re: /session\s+(reset|was reset|cleared)/i,               icon: '⏳', label: 'Session refreshed',       category: 'refresh' },
  { re: /ephemeral\s+reset/i,                                 icon: '⏳', label: 'Session refreshed',       category: 'refresh' },
  { re: /turn\s+limit\s+(reached|exceeded)/i,                 icon: '⏳', label: 'Session refreshed',       category: 'refresh' },
  { re: /max\s*turns?\s*(reached|exceeded|limit)/i,           icon: '⏳', label: 'Session refreshed',       category: 'refresh' },
  { re: /auto[- ]?reset\s*[·•]\s*(turn|ctx)/i,               icon: '⏳', label: 'Session refreshed',       category: 'refresh' },

  // Still working / slow query
  { re: /claude\s+is\s+still\s+working/i,                     icon: '⚙',  label: 'Still working...',        category: 'info' },
  { re: /agent\s+working\s+for/i,                             icon: '⚙',  label: 'Still working...',        category: 'info' },
  { re: /streaming\s+will\s+resume/i,                         icon: '⚙',  label: 'Resuming stream...',      category: 'info' },
  { re: /finished\s+while\s+UI\s+was\s+disconnected/i,        icon: '⚙',  label: 'Finished offline',        category: 'info' },
  // Close-out pill appended by useSocket.ts CLAUDE_DONE when an unresolved
  // "⏳ still working" pill is present — visually closes the timeline so the
  // stale in-progress indicator can't be misread as "agent is busy right now."
  { re: /streaming\s+resumed.*agent\s+finished/i,             icon: '✓',  label: 'Resumed',                 category: 'save' },

  // Docs update / knowledge save
  { re: /persisting\s+knowledge/i,                            icon: '📝', label: 'Notes saved',             category: 'save' },
  { re: /docs[- ]?update/i,                                   icon: '📝', label: 'Notes saved',             category: 'save' },
  { re: /knowledge\s+saved/i,                                 icon: '📝', label: 'Notes saved',             category: 'save' },
  { re: /✓\s*knowledge\s+saved/i,                             icon: '📝', label: 'Notes saved',             category: 'save' },
  { re: /fresh\s+session\s+ready/i,                           icon: '📝', label: 'Notes saved',             category: 'save' },

  // Prompt cancel
  { re: /prompt\s+cancell?ed/i,                               icon: '✕',  label: 'Cancelled',              category: 'info' },

  // Session resume failures / fresh start
  { re: /session\s+resume\s+failed/i,                         icon: '🔄', label: 'Fresh start',             category: 'refresh' },
  { re: /no\s+conversation\s+found/i,                         icon: '🔄', label: 'Fresh start',             category: 'refresh' },
  { re: /stale\s+session/i,                                   icon: '🔄', label: 'Reconnected',             category: 'refresh' },
  { re: /reconnect/i,                                         icon: '🔄', label: 'Reconnected',             category: 'refresh' },

  // Context compacted
  { re: /context\s+compact/i,                                 icon: '⏳', label: 'Session refreshed',       category: 'refresh' },

  // Provider switch / routing
  { re: /switching\s+to\s+(claude|gemini|sonnet|opus|haiku)/i, icon: '⇄',  label: 'Switching model',        category: 'info' },
  { re: /smart\s+routing\s+offline/i,                         icon: '⇄',  label: 'Routing degraded',        category: 'error' },
  { re: /provider\s+switch/i,                                 icon: '⇄',  label: 'Model switched',          category: 'info' },

  // Rate limit
  { re: /rate\s+limit\s+hit/i,                                icon: '⏱',  label: 'Rate limited',            category: 'error' },
  { re: /approaching\s+api\s+rate\s+limit/i,                  icon: '⏱',  label: 'Approaching rate limit',  category: 'error' },
  { re: /api\s+rate\s+limit/i,                                icon: '⏱',  label: 'Rate limited',            category: 'error' },

  // Cascade / SDK errors
  { re: /response\s+may\s+be\s+incomplete/i,                  icon: '⚠',  label: 'Response cut short',      category: 'error' },
  { re: /request\s+(failed|rejected)/i,                       icon: '⚠',  label: 'Request failed',          category: 'error' },
  { re: /sdk\s+error/i,                                       icon: '⚠',  label: 'SDK error',               category: 'error' },

  // Generic error (keep last — catches anything with "error/failed/exception")
  { re: /error|failed|exception/i,                            icon: '⚠',  label: 'Issue encountered',       category: 'error' },
];

/**
 * Try to match a system message to a friendly label.
 * Returns null if no pattern matches (message renders as-is).
 */
export function humanizeSystemEvent(text: string): HumanizedEvent | null {
  if (!text) return null;
  for (const p of PATTERNS) {
    if (p.re.test(text)) {
      return { icon: p.icon, label: p.label, detail: text, category: p.category };
    }
  }
  return null;
}
